// Brookline Department of Public Works — 10-Year Strategic Plan (2022–2032)
// Source: Town of Brookline, Department of Public Works Strategic Plan 2022–2032
//   (Final update 2023). The plan defines the Department's vision, mission, core
//   values, and five Guiding Principles. This page presents the five Guiding
//   Principles and, under each, the plan's objectives and lettered action items,
//   drawn near-verbatim from the plan. Objective and action lettering follows the
//   source (which skips some letters, e.g. there is no 2.2B or 2.3A, and several
//   objectives are stated without lettered actions). The Department profiles of the
//   five operating divisions are in the source document.
// Page created August 2026.

export const dpwStrategicData = {
  planYears: '2022–2032',
  updatedDate: 'August 2026',
  introduction: {
    overview: [
      'The Brookline Department of Public Works 10-Year Strategic Plan (2022–2032) is a working blueprint for the Department, defining its vision, mission, core values, and five Guiding Principles, and how it expects to achieve them over the next ten years.',
      'The Department’s vision is to provide the foundation for a sustainable, inclusive, connected, vibrant, safe and livable community. Its five core values are that DPW is Collaborative, Professional, Innovative, Inclusive, and Accountable.',
      'This page presents the plan’s five Guiding Principles and, under each, the objectives and lettered action items that carry them out. The plan is intended to be reviewed each year as measurable goals are developed during the Town’s financial planning process, and updated every four years. The Department’s division profiles and supporting detail are in the source document.'
    ],
    itemsNote: [
      'Each Guiding Principle is carried out by numbered objectives (for example, 1.1, 1.2), and most objectives list lettered action items (for example, 1.1A).',
      'The objective and action lettering follows the source plan, which skips some letters and states several objectives without lettered actions.'
    ]
  },
  principles: [
    {
      id: 'network-of-public-spaces',
      number: 1,
      name: 'Provide a clean, green, safe, accessible, inclusive, sustainable and welcoming network of public spaces, assets, utilities and infrastructure.',
      statement: 'Brookline is a desirable Town in which to live, work and visit because the Department supports clean, green, safe, accessible and inviting public spaces and right-of-way assets and supports well-maintained and reliable utilities and infrastructure.',
      objectives: [
        {
          id: '1.1',
          title: 'Provide a safe, efficient, and reliable public right-of-way network that supports sustainable, convenient, and accessible multi- and micro-modal transportation options.',
          actions: [
            '1.1A Develop a Transportation and Mobility Plan.',
            '1.1B Develop, define and prioritize opportunities for transportation innovation.',
            '1.1C Create a pedestrian network to enable pedestrians of all ages and abilities to move more comfortably between places and destinations.',
            '1.1D Establish a bike and micro-mobility device network that integrates the on-street and off-street system to provide safe, comfortable corridors for Brookline residents and visitors of all ages and abilities to connect to a variety of destinations including parks, open spaces, public buildings, commercial areas and regional routes.',
            '1.1E Partner with the MBTA to support efficient, equitable, and attractive public transit services to all residents, workers and visitors.',
            '1.1F Support an electric vehicle infrastructure master plan that supports charging of public and private vehicles.',
            '1.1G Develop a Construction Management Plan standard and template to proactively minimize construction impacts and maintain accessible and safe passage on the right-of-way.',
            '1.1H Digitize, formalize and document best practice for the right-of-way permit process to ensure compliance, inspection and enforcement to protect Town infrastructure.',
            '1.1I Develop a program to inspect, repair and/or upgrade infrastructure in coordination with public right-of-way renovation and rehabilitation projects.'
          ]
        },
        {
          id: '1.2',
          title: 'Enhance the cleanliness and livability of the Town.',
          actions: [
            '1.2A Coordinate resources to enhance the quality and care of infrastructure and assets within the public realm to make the Town more attractive, clean, active and pedestrian/bike/micro-mobility friendly.',
            '1.2B Develop costs and framework for a civic space beautification program.',
            '1.2C Create initiatives and partnerships to increase resources and capacity in targeted commercial areas and neighborhoods.',
            '1.2D Use community engagement and outreach strategies to strengthen education of and compliance with bylaws, regulations, rules and regulations in the public realm.'
          ]
        },
        {
          id: '1.3',
          title: 'Provide beautiful, highly functional, accessible and sustainable parks, public grounds and public spaces to create opportunities for recreation, connection, improved environmental and public health, joy and belonging.',
          actions: [
            '1.3A Coordinate resources to enhance quality and care of the public realm to make the areas more attractive, clean, active and pedestrian/bike/micro-mobility friendly.',
            '1.3B Renovate and design parks and public spaces to reflect industry standards, support the Town’s climate action goals, and meet the needs and desires of the community.',
            '1.3C Provide an excellent standard of care for Brookline’s parks, public grounds and public spaces.',
            '1.3D Improve access to, through and within public parks, public grounds and public spaces.',
            '1.3E Develop public art guidelines and launch a community program to increase public art and opportunities for local artists to enhance public spaces.'
          ]
        }
      ]
    },
    {
      id: 'service-provider-of-choice',
      number: 2,
      name: 'Be the service provider of choice for design, construction, maintenance, and management.',
      statement: 'The Department professionally and cost-effectively addresses current and future needs through creativity, innovation, and skill in order to maintain and enhance the community’s essential infrastructure, assets, utilities and natural resources.',
      objectives: [
        {
          id: '2.1',
          title: 'Maintain and improve public property, assets, utilities and infrastructure to ensure long-term sustainability, to exceed public expectations and ensure the public trust.',
          actions: [
            '2.1A Implement projects that improve efficiency, reduce costs, benefit the environment, improve quality, extend life cycle, and/or improve operational processes.',
            '2.1B Develop measuring tools to assess progress and success.',
            '2.1C Create an interdisciplinary review process for multi-stakeholder projects with input for design, constructability, climate resilience design standards and maintainability.',
            '2.1D Create a standard operating procedure to coordinate in-house and contractor repairs with planned projects to maximize efficiency, value and public benefit.'
          ]
        },
        {
          id: '2.2',
          title: 'Collaborate, engage and expand Department thinking about processes, policies and systems to improve planning, efficiency, innovation, project outcomes and service delivery to the Town.',
          actions: [
            '2.2A Create and implement a digital transformation strategy across all functions and upgrade Public Works IT systems.',
            '2.2C Improve core business deliverables and efficiency by effectively engaging and partnering with Town Divisions and Departments to better serve the public and support broader municipal goals.',
            '2.2D Engage Departments/Divisions to identify and review challenges on project delivery and solicit their feedback towards establishing a collaborative culture of continuous improvement and the highest standards of customer service and project delivery.',
            '2.2E Improve communication, clarify roles and implement SOPs to achieve a higher standard of project delivery, better decision-making processes, and minimize impacts to budget and schedule.',
            '2.2F Increase citizen understanding of the function, practices and procedures of the Public Works Department.',
            '2.2G Lead bi-annual educational/information-sharing campaigns in partnership with non-profits, neighborhood associations, and other Town Departments to help the public understand DPW’s scope of work, function and mission.'
          ]
        },
        {
          id: '2.3',
          title: 'Identify and actively seek federal, state and local grant opportunities to leverage Town funds and improve Brookline’s utilities, infrastructure, natural resources, programs and services.',
          actions: [
            '2.3B Collaborate with other jurisdictions, agencies and non-profit organizations to promote information exchange, best practice, collaboration, and cooperation.'
          ]
        }
      ]
    },
    {
      id: 'culture-of-excellence',
      number: 3,
      name: 'Create a culture of opportunity, continuous improvement and excellence to deliver exceptional public projects and services.',
      statement: 'All Public Works employees will be highly qualified individuals and provided with the opportunity to continually increase their knowledge through experience, education and training in order to provide the highest level of customer service and project outcomes.',
      objectives: [
        {
          id: '3.1',
          title: 'Achieve a high level of customer service and satisfaction.',
          actions: [
            '3.1A Develop a Department of Public Works Communications Plan.',
            '3.1B Develop customer service feedback tools and methods to measure customer satisfaction.',
            '3.1C Provide customer-service-related training to all Public Works employees.'
          ]
        },
        {
          id: '3.2',
          title: 'Retain, develop and recruit a capable, motivated and diverse workforce.',
          actions: [
            '3.2A Create standard recruiting, hiring, and promotion guidelines that ensure the Department is building a knowledgeable and inclusive workforce that receives regular and constructive feedback from their supervisors.',
            '3.2B Ensure Public Works employees are trained, mentored and prepared to perform all of their functions.',
            '3.2C Develop and implement a succession plan to effectively recruit, train and retain well-qualified employees and ensure critical staffing needs are met.',
            '3.2D Review compensation plans and job descriptions to ensure they are appropriate for the market and can attract and retain qualified and competent employees for the Department.'
          ]
        },
        {
          id: '3.3',
          title: 'Build a culture of collaboration, support, and appreciation for all DPW employees.',
          actions: [
            '3.3A Develop and implement a Public Works Values/Code of Conduct.',
            '3.3B Provide training and set expectations to help employees communicate effectively and respectfully between and among colleagues and supervisors.',
            '3.3C Develop an employee recognition program with an emphasis on customer service, innovative problem solving, exceptional performance and safety.',
            '3.3D Research and submit applicable projects for award opportunities across all five Divisions.'
          ]
        },
        {
          id: '3.4',
          title: 'Prioritize the health and safety of Department employees and the public as part of our daily performance, deliverables and operations.',
          actions: [
            '3.4A Create a culture of occupational health and safety across all functions.',
            '3.4B Ensure that all public works employees have a healthy, safe and secure workplace.',
            '3.4C Develop an innovative and effective environmental, health and safety program.',
            '3.4D Strive to have zero preventable accidents each year.',
            '3.4E Appoint a multi-Division team to plan and implement an annual safety training program.',
            '3.4F Provide appropriate equipment and facilities for employees to work efficiently and safely.'
          ]
        }
      ]
    },
    {
      id: 'inclusion-equity-engagement',
      number: 4,
      name: 'Demonstrate inclusion, equity and engagement values in our management of public assets, services and employment practices.',
      statement: 'Systemic racism has shaped the country, including Brookline, and is a profound barrier to achieving our goals. Undoing it is part of our vision and our future.',
      objectives: [
        {
          id: '4.1',
          title: 'Increase diversity and inclusion in the hiring and retention of DPW employees.',
          actions: [
            '4.1A Ensure all managers and supervisors receive an annual Diversity, Equity and Inclusion training to support the hiring and retention of BIPOC (Black, Indigenous, People of Color) individuals.',
            '4.1B Work with Human Resources to ensure all job postings reflect DPW’s commitment to diversity, equity and inclusion.',
            '4.1C Annually evaluate the number of positions that have opened up and been filled with a BIPOC individual.'
          ]
        },
        {
          id: '4.2',
          title: 'Support the racial, ethnic, and age diversification of all public boards and commissions that serve as advisory boards to DPW.',
          actions: []
        },
        {
          id: '4.3',
          title: 'Implement a Diversity, Equity, and Inclusion continuing education program for all DPW staff that interface with the public.',
          actions: []
        },
        {
          id: '4.4',
          title: 'Increase opportunities for WMBE (Women- and Minority-owned Business Enterprises) for construction and professional service contracts.',
          actions: []
        },
        {
          id: '4.5',
          title: 'Create places and provide services that are equitable, sustainable, welcoming to all and support diversity, inclusion, public health, transportation and community life.',
          actions: [
            '4.5A Implement a community engagement process that involves historically underrepresented and vulnerable residents in participatory design to create spaces that offer refuge, reflection and inclusiveness.',
            '4.5B Support the Town’s diversity, equity, and community engagement goals through a parks, public grounds and public right-of-way network that is welcoming and accessible to all and supports positive outcomes in Environmental Justice areas.'
          ]
        }
      ]
    },
    {
      id: 'climate-mitigation-and-adaptation',
      number: 5,
      name: 'Incorporate climate mitigation and adaptation goals into the development of Capital Improvement Projects and daily public works operations.',
      statement: 'As stewards of the environment, the Department advances a sustainable Town that is prosperous, clean, safe, healthy, and promotes greenhouse gas reduction and natural resource conservation and stewardship in all aspects of its operation.',
      objectives: [
        {
          id: '5.1',
          title: 'Enhance the sustainability of the Town’s public spaces and business practices.',
          actions: [
            '5.1A Implement Brookline’s Urban Forest Climate Resiliency Master Plan.',
            '5.1B Develop a Green Fleet Management & Infrastructure Plan that prioritizes fleet efficiency, maintenance and reliability.',
            '5.1C Develop a Zero Waste Framework.',
            '5.1D Implement and promote paperless permitting processes.',
            '5.1E Identify and implement strategies to increase the use of recycled water, materials and approved eco-friendly products.',
            '5.1F Identify public spaces that could be upgraded to Leadership in Energy and Environmental Design (LEED) standards or made more sustainable.',
            '5.1G Develop materials management guidelines and purchasing practices.'
          ]
        },
        {
          id: '5.2',
          title: 'Proactively comply with or exceed environmental and infrastructure quality standards in sanitary sewer system maintenance and management.',
          actions: []
        },
        {
          id: '5.3',
          title: 'Develop, coordinate and implement a program that promotes a sustainable approach to stormwater runoff, flood reduction, and proactively meets or exceeds current environmental quality standards and regulatory requirements (MS4 Permit).',
          actions: [
            '5.3A Develop a green infrastructure implementation and maintenance plan that increases phosphorous reduction, flood resilience and heat island reduction.',
            '5.3B Create an awareness campaign that educates public and private partners and residents about water quality impairments and best management practices, and outlines a call to action through educational brochures, materials and public meetings/events.',
            '5.3C Complete the Muddy River Flood Control Project.',
            '5.3D Demonstrate improved water quality at outflows to the Muddy River through documented investigation, sampling, capital improvements and best management practices.'
          ]
        },
        {
          id: '5.4',
          title: 'Develop an irrigation and water management program for the Town’s municipal water use to monitor and reduce water consumption, ensure efficient and effective utilization, and evaluate and recommend changes in use or system infrastructure improvements.',
          actions: []
        },
        {
          id: '5.5',
          title: 'Support the implementation of Brookline’s Climate Action Plan (2018) and the Healthy and Sustainable Warrant Article 31 (2019).',
          actions: []
        }
      ]
    }
  ]
};
